import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import RadioGroup from 'components/form/RadioGroup';
import TextInput from 'components/form/TextInput';
import CollapsableSection from '../CollapsableSection';

const ConfirmationPage = ({ data, onInputChange }) => {
  const {
    confirmation_video_url,
    confirmation_message,
    pixel_id,
    google_analytics_id,
    confirmation_custom_url,
  } = data;

  const [t] = useTranslation('groups');
  const [tab, setTab] = useState(
    confirmation_custom_url ? 'custom' : 'default',
  );
  const [expanded, setExpanded] = useState(false);

  const onTypeChange = (e) => {
    const { value } = e.target;
    setTab(value);
  };

  return (
    <Box>
      <CollapsableSection
        title="Advanced Settings (Optional)"
        isActive={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <Box>
          <Box mb={3}>
            <Typography paragraph fontWeight={500}>
              {t(
                'cc-form-confirm-page-title',
                'Customize Your Confirmation Page',
              )}
            </Typography>
            <Box sx={{ width: '100%', typography: 'body1' }} mb={4}>
              <Box>
                <RadioGroup
                  row
                  value={tab}
                  options={[
                    {
                      label: 'Use Default Confirmation Page',
                      value: 'default',
                    },
                    {
                      label: 'Redirect to Custom Confirmation',
                      value: 'custom',
                    },
                  ]}
                  onChange={onTypeChange}
                />
              </Box>
              {tab === 'default' && (
                <Box>
                  <TextInput
                    fullWidth
                    name="confirmation_video_url"
                    value={confirmation_video_url}
                    onChange={onInputChange}
                    label={
                      <Box display={'flex'}>
                        <Box mr="auto">
                          {t(
                            'cc-form-confirm-video-label',
                            'Confirmation Page Video URL',
                          )}
                        </Box>
                        <MuiLink
                          href="https://help.textinchurch.com/en/articles/2999495-supported-video-sources"
                          target="_blank"
                        >
                          {t('cc-form-video-support', 'What is supported?')}
                        </MuiLink>
                      </Box>
                    }
                    placeholder={t(
                      'cc-form-video-placeholder',
                      'Vimeo or Youtube URL',
                    )}
                  />
                  <TextInput
                    fullWidth
                    name="confirmation_message"
                    value={confirmation_message}
                    onChange={onInputChange}
                    label={t(
                      'cc-form-confirm-msg-label',
                      'Add Custom Confirmation Message',
                    )}
                    placeholder={t(
                      'cc-form-confirm-msg-placeholder',
                      'Thank you for filling out our card...',
                    )}
                  />
                </Box>
              )}
              {tab === 'custom' && (
                <Box>
                  <TextInput
                    fullWidth
                    label={t(
                      'cc-form-confirm-redirect-title',
                      'Redirect to Custom Confirmation Page',
                    )}
                    hint={t(
                      'cc-form-confirm-redirect-desc',
                      'By default a confirmation page will show after submission of the form. You can instead redirect to a thank you page if you wish.',
                    )}
                    name="confirmation_custom_url"
                    value={confirmation_custom_url}
                    onChange={onInputChange}
                    placeholder="http://"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box mt={4}>
          <Box>
            <Box mb={2}>
              <Typography paragraph fontWeight={500}>
                Add Custom Tracking
              </Typography>
            </Box>
            <Box>
              <TextInput
                fullWidth
                name="pixel_id"
                value={pixel_id}
                onChange={onInputChange}
                label={
                  <Box display="flex">
                    <Typography mr="auto">
                      {t('cc-form-fb-pixel', 'Facebook Tracking Pixel ID')}
                    </Typography>
                    <MuiLink
                      href="https://help.textinchurch.com/en/articles/2999504-how-to-find-your-facebook-tracking-pixel-id"
                      target="_blank"
                    >
                      {t('cc-form-fb-pixel-how', 'How do I find it?')}
                    </MuiLink>
                  </Box>
                }
              />
              <TextInput
                fullWidth
                name="google_analytics_id"
                value={google_analytics_id}
                onChange={onInputChange}
                label={
                  <Box display="flex">
                    <Box mr="auto">
                      {t('cc-form-google-code', 'Google Analytics Tracking ID')}
                    </Box>
                    <MuiLink
                      href="https://help.textinchurch.com/en/articles/4361059-how-to-find-your-google-tracking-pixel"
                      target="_blank"
                    >
                      {t('cc-form-fb-pixel-how', 'How do I find it?')}
                    </MuiLink>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>
      </CollapsableSection>
    </Box>
  );
};

export default ConfirmationPage;
